// Official Google Identity Services "Sign in with Google" button —
// pixel- and colour-faithful to Google's branding spec. Required for
// OAuth consent flows per Google's branding guidelines so the button
// the user clicks matches what Google's identity team expects.
//
// Used on:
//   - /app/connect — primary CTA ("Continue with Google to connect GA4")
//   - /app/settings — offline-access upgrade ("Continue with Google for
//     extended access")
//
// CSS lives in `src/client/styles/index.css` under `.gsi-material-button`
// so it applies globally without per-component style imports.

import type { ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

type NativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

export interface GoogleSignInButtonProps extends Omit<NativeButtonProps, "children" | "className"> {
  /** The visible button label, e.g. "Continue with Google to connect
   *  GA4". Google's branding guidelines require text starting with one
   *  of: "Sign in with Google", "Sign up with Google", "Continue with
   *  Google", "Sign in", "Sign up". We use "Continue with Google" so
   *  the label can carry context without breaking compliance. */
  label: string;
  /** When true, the button stretches to fill its container. The bare
   *  Google button is auto-width by default; the connect-page CTA wants
   *  full-width. Defaults to false. */
  fullWidth?: boolean;
  /** Optional extra classes — appended to the canonical
   *  `.gsi-material-button` class. Use sparingly; prefer wrapping. */
  className?: string;
}

export function GoogleSignInButton({
  label,
  fullWidth = false,
  className,
  type = "button",
  ...rest
}: GoogleSignInButtonProps) {
  return (
    <button
      type={type}
      className={cn("gsi-material-button", fullWidth && "w-full max-w-none", className)}
      {...rest}
    >
      <div className="gsi-material-button-state" />
      <div className="gsi-material-button-content-wrapper">
        <div className="gsi-material-button-icon">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            style={{ display: "block" }}
            aria-hidden="true"
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
            <path fill="none" d="M0 0h48v48H0z" />
          </svg>
        </div>
        <span className="gsi-material-button-contents">{label}</span>
        {/* Google's spec keeps a hidden duplicate of the label for
         *  accessibility tools that prefer the second span. Match the
         *  reference markup verbatim. */}
        <span style={{ display: "none" }}>{label}</span>
      </div>
    </button>
  );
}
